import { h, Component } from 'preact';
import { Link } from 'preact-router/match';
import style from './style';
import { Stateful } from '@components/pubsub';
import { Button } from '@components/button';
import I18n from '@i18n';
import { slugify, getCity, setCity } from '@helpers';
import Image from '@components/image';
import Text from '@components/translate';
import { API } from '@globals';

class MobileNav extends Component {
  state = {
    showLanguageSelector: false,
    showCitySelector: false,
    cities: [],
    showGradient: true,
    scrollPercent: 0,
  };

  constructor(props) {
    super(props);
    this.dropdownRef = null;
  }

  fetchCities = () => {
    this.context.auth.request(`${API}/cities.json`).then(allCities => {
      this.setState({ cities: allCities });
    });
  };

  componentDidMount() {
    if (!this.state.cities.length) {
      this.fetchCities();
    }
  }

  toggleLanguageSelector = e => {
    e.stopPropagation();
    this.setState({
      showLanguageSelector: !this.state.showLanguageSelector,
      showCitySelector: false,
    });
  };

  toggleCitySelector = e => {
    e.stopPropagation();
    this.setState({
      showCitySelector: !this.state.showCitySelector,
      showLanguageSelector: false,
    });
  };

  selectLanguage = lang => {
    // Handle language change
    window.location.pathname = window.location.pathname
      .split('/')
      .map((item, index) => (index === 1 ? lang : item))
      .join('/');

    this.setState({ showLanguageSelector: false });
  };

  selectCity = city => {
    setCity(city);
    if (this.context.events) {
      this.context.events.emit('city', city);
    }
    this.setState({ showCitySelector: false });
  };

  closeMenu = () => {
    // Call the onClose prop provided by the parent
    if (this.props.onClose) {
      this.props.onClose();
    }

    this.setState({
      showLanguageSelector: false,
      showCitySelector: false,
    });
    document.body.style.overflow = 'auto';
  };

  handleDropdownScroll = () => {
    if (!this.dropdownRef) return;
    const { scrollTop, scrollHeight, clientHeight } = this.dropdownRef;
    // If scrolled to bottom, hide gradient
    if (scrollTop + clientHeight >= scrollHeight - 1) {
      if (this.state.showGradient) this.setState({ showGradient: false });
    } else {
      if (!this.state.showGradient) this.setState({ showGradient: true });
    }

    const scrollPercent = scrollTop / (scrollHeight - clientHeight);
    this.setState({ scrollPercent });
  };

  componentDidUpdate(prevProps, prevState) {
    // Handle body overflow when isOpen changes
    if (prevProps.isOpen !== this.props.isOpen) {
      document.body.style.overflow = this.props.isOpen ? 'hidden' : 'auto';
    }
    // Attach/detach scroll listener for city dropdown
    if (prevState.showCitySelector !== this.state.showCitySelector) {
      if (this.state.showCitySelector && this.dropdownRef) {
        this.dropdownRef.addEventListener('scroll', this.handleDropdownScroll);
        // Initial check in case already at bottom
        this.handleDropdownScroll();
      } else if (this.dropdownRef) {
        this.dropdownRef.removeEventListener(
          'scroll',
          this.handleDropdownScroll
        );
        // Reset gradient when closed
        if (!this.state.showGradient) this.setState({ showGradient: true });
      }
    }
  }

  render(
    { isOpen = false, onClose },
    {
      showLanguageSelector,
      showCitySelector,
      cities,
      showGradient,
      scrollPercent,
    },
    { lang, events }
  ) {
    const languages = [
      { code: 'en', name: 'English', flag: 'gb' },
      { code: 'fi', name: 'Suomi', flag: 'fi' },
      { code: 'ee', name: 'Eesti', flag: 'ee' },
    ];

    // Fallback cities if API request hasn't completed yet
    const defaultCities = [
      'Helsinki',
      'Espoo',
      'Tampere',
      'Turku',
      'Tallinn',
      'Oulu',
    ];
    const availableCities = cities.length ? cities : defaultCities;

    const currentLanguage =
      languages.find(l => l.code === lang) || languages[0];

    // Get appropriate app store badges based on language
    const getAppStoreImage = () => {
      switch (lang) {
        case 'fi':
          return '/assets/download/Download_on_the_App_Store_Badge_FI_RGB_blk_100217.svg';
        case 'ee':
          return '/assets/download/Download_on_the_App_Store_Badge_EE_RGB_blk_100217.svg';
        case 'en':
        default:
          return '/assets/download/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg';
      }
    };

    const getGooglePlayImage = () => {
      switch (lang) {
        case 'fi':
          return '/assets/download/GetItOnGooglePlay_Badge_Web_color_Finnish.png';
        case 'ee':
          return '/assets/download/GetItOnGooglePlay_Badge_Web_color_Estonian.png';
        case 'en':
        default:
          return '/assets/download/GetItOnGooglePlay_Badge_Web_color_English.png';
      }
    };

    // Localized store URLs
    const getAppStoreUrl = () => {
      switch (lang) {
        case 'fi':
          return 'https://apps.apple.com/fi/app/tableonline-reservations/id1596078295';
        case 'ee':
          return 'https://apps.apple.com/ee/app/tableonline-reservations/id1596078295';
        case 'en':
        default:
          return 'https://apps.apple.com/app/tableonline-reservations/id1596078295';
      }
    };

    const getGooglePlayUrl = () => {
      switch (lang) {
        case 'fi':
          return 'https://play.google.com/store/apps/details?id=fi.tableonline.app&hl=fi';
        case 'ee':
          return 'https://play.google.com/store/apps/details?id=fi.tableonline.app&hl=et';
        case 'en':
        default:
          return 'https://play.google.com/store/apps/details?id=fi.tableonline.app&hl=en';
      }
    };

    return (
      <div class={style.mobileNavContainer}>
        {isOpen && <div class={style.backsplash} onClick={this.closeMenu} />}

        <div class={`${style.mobileMenu} ${isOpen ? style.open : ''}`}>
          <div class={style.mobileMenuHeader}>
            <img
              src="/assets/logo/TableOnline_Logo_Green.svg"
              alt="TableOnline Icon"
              class={style.tableOnlineIcon}
            />
            <button class={style.closeButton} onClick={this.closeMenu}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div class={style.mobileMenuContent}>
            {/* Mobile App Download Section */}
            <div class={style.appDownloadSection}>
              <div class={style.appDownloadLinks}>
                <a
                  href={getAppStoreUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  class={style.storeLink}
                  onClick={this.closeMenu}
                >
                  <img
                    src={getAppStoreImage()}
                    alt="Download on the App Store"
                    class={style.storeBadge}
                  />
                </a>
                <a
                  href={getGooglePlayUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  class={style.storeLink}
                  onClick={this.closeMenu}
                >
                  <img
                    src={getGooglePlayImage()}
                    alt="Get it on Google Play"
                    class={style.storeBadge}
                  />
                </a>
              </div>
            </div>
            <div class={style.navSection}>
              <div class={style.navItem} onClick={this.toggleLanguageSelector}>
                <div class={style.navItemContent}>
                  <div class={style.navIconWrapper}>
                    <span class={style.languageSelectorFlag}>
                      <Image
                        src={`https://cdn.rawgit.com/hjnilsson/country-flags/master/svg/${currentLanguage.flag}.svg`}
                        alt={currentLanguage.name}
                        class={style.languageSelectorFlagImage}
                      />
                    </span>
                  </div>
                  <span>{currentLanguage.name}</span>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class={`${style.chevron} ${
                    showLanguageSelector ? style.up : ''
                  }`}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>

              {showLanguageSelector && (
                <div class={style.dropdownContent}>
                  {languages.map(language => (
                    <div
                      key={language.code}
                      class={style.dropdownItem}
                      onClick={() => this.selectLanguage(language.code)}
                    >
                      <span class={style.languageSelectorFlag}>
                        <Image
                          src={`https://cdn.rawgit.com/hjnilsson/country-flags/master/svg/${language.flag}.svg`}
                          alt={language.name}
                          class={style.languageSelectorFlagImage}
                        />
                      </span>
                      <span>{language.name}</span>
                    </div>
                  ))}
                </div>
              )}

              <Stateful events={['city']}>
                {({ city = getCity() }) => (
                  <div
                    class={style.citySelectorWrapper}
                    showGradient={showCitySelector && showGradient}
                  >
                    <div
                      class={style.navItem}
                      onClick={this.toggleCitySelector}
                    >
                      <div class={style.navItemContent}>
                        <div class={style.navIconWrapper}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M12 0c-4.198 0-8 3.403-8 7.602 0 4.198 3.469 9.21 8 16.398 4.531-7.188 8-12.2 8-16.398 0-4.199-3.801-7.602-8-7.602zm0 11c-1.657 0-3-1.343-3-3s1.343-3 3-3 3 1.343 3 3-1.343 3-3 3z" />
                          </svg>
                        </div>
                        <span>{city}</span>
                      </div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class={`${style.chevron} ${
                          showCitySelector ? style.up : ''
                        }`}
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </div>

                    {showCitySelector && (
                      <div
                        class={style.dropdownContent}
                        ref={el => (this.dropdownRef = el)}
                        style={{ maxHeight: '250px', overflowY: 'auto' }}
                      >
                        {availableCities.map(city => (
                          <a
                            href={`/${lang}/${city.slug}`}
                            key={city.title}
                            class={style.dropdownItem}
                            onClick={this.closeMenu}
                          >
                            {city.title}
                          </a>
                        ))}
                      </div>
                    )}

                    {showCitySelector && (
                      <div
                        class={style.scrollIndicator}
                        style={{ top: `${(250 - 24) * scrollPercent + 56}px` }}
                      />
                    )}
                  </div>
                )}
              </Stateful>

              <a
                href={
                  lang === 'fi'
                    ? `/fi/liity-tableonlineen`
                    : lang === 'en'
                    ? `/en/join`
                    : lang === 'ee'
                    ? `/ee/vota-meiega-uhendust`
                    : null
                }
                class={style.navItem}
                onClick={this.closeMenu}
              >
                <div class={style.navItemContent}>
                  <div class={style.navIconWrapper}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 -2.89 122.88 122.88"
                      fill="currentColor"
                    >
                      <g>
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M36.82,107.86L35.65,78.4l13.25-0.53c5.66,0.78,11.39,3.61,17.15,6.92l10.29-0.41c4.67,0.1,7.3,4.72,2.89,8 c-3.5,2.79-8.27,2.83-13.17,2.58c-3.37-0.03-3.34,4.5,0.17,4.37c1.22,0.05,2.54-0.29,3.69-0.34c6.09-0.25,11.06-1.61,13.94-6.55 l1.4-3.66l15.01-8.2c7.56-2.83,12.65,4.3,7.23,10.1c-10.77,8.51-21.2,16.27-32.62,22.09c-8.24,5.47-16.7,5.64-25.34,1.01 L36.82,107.86L36.82,107.86z M29.74,62.97h91.9c0.68,0,1.24,0.57,1.24,1.24v5.41c0,0.67-0.56,1.24-1.24,1.24h-91.9 c-0.68,0-1.24-0.56-1.24-1.24v-5.41C28.5,63.53,29.06,62.97,29.74,62.97L29.74,62.97z M79.26,11.23 c25.16,2.01,46.35,23.16,43.22,48.06l-93.57,0C25.82,34.23,47.09,13.05,72.43,11.2V7.14l-4,0c-0.7,0-1.28-0.58-1.28-1.28V1.28 c0-0.7,0.57-1.28,1.28-1.28h14.72c0.7,0,1.28,0.58,1.28,1.28v4.58c0,0.7-0.58,1.28-1.28,1.28h-3.89L79.26,11.23L79.26,11.23 L79.26,11.23z M0,77.39l31.55-1.66l1.4,35.25L1.4,112.63L0,77.39L0,77.39z"
                        />
                      </g>
                    </svg>
                  </div>
                  <span>
                    <Text>{I18n.t("For restaurants")}</Text>
                  </span>
                </div>
              </a>

              <Link
                href={
                  lang === 'fi'
                    ? `/fi/ota-yhteytta`
                    : lang === 'en'
                    ? `/en/contact-us`
                    : lang === 'ee'
                    ? `/ee/vota-meiega-uhendust`
                    : null
                }
                class={style.navItem}
                onClick={() => {
                  this.closeMenu();
                  trackContentClick('Mobile nav link', 'Contact us');
                }}
              >
                <div class={style.navItemContent}>
                  <div class={style.navIconWrapper}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 24c6.627 0 12-5.373 12-12s-5.373-12-12-12-12 5.373-12 12 5.373 12 12 12zm1-6h-2v-8h2v8zm-1-12.25c.69 0 1.25.56 1.25 1.25s-.56 1.25-1.25 1.25-1.25-.56-1.25-1.25.56-1.25 1.25-1.25z" />
                    </svg>
                  </div>
                  <span>
                    <Text>{I18n.t("Contact us")}</Text>
                  </span>
                </div>
              </Link>

              <Stateful events={['auth']}>
                {({ auth }) =>
                  auth ? (
                    <Link
                      href={`/${lang}/profile`}
                      class={style.navItem}
                      onClick={this.closeMenu}
                    >
                      <div class={style.navItemContent}>
                        <div class={style.navIconWrapper}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                        </div>
                        <span>
                          {auth.firstname} {auth.lastname}
                        </span>
                      </div>
                    </Link>
                  ) : (
                    <div class={style.profileSection}>
                      <div class={style.authButtons}>
                        <Button
                          href={`/${lang}/profile`}
                          class={style.loginButton}
                          onClick={this.closeMenu}
                        >
                          {I18n.t('Login')}
                        </Button>
                        <Button
                          href={`/${lang}/profile?view=create`}
                          class="defaultButton"
                          onClick={this.closeMenu}
                        >
                          {I18n.t('Create account')}
                        </Button>
                      </div>
                    </div>
                  )
                }
              </Stateful>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default MobileNav;



// WEBPACK FOOTER //
// ./components/mobile-nav/index.js